import cx from 'classnames'
import * as Yup from 'yup'
import { useLocation } from 'react-router'
import { Form, Formik } from 'formik'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'
import { countryCodeList, getCountryLabel, prefectureList } from 'config'
import { getGenderLabel } from 'config'
import { useBanWord, useUploadImage } from 'hooks'
import { Link, useNavigate } from 'features/i18n/router'
import {
  useCurrentUser,
  useDeleteUserCoverImageMutation,
  useDeleteUserProfileImageMutation,
  useUpdateUserV3Mutation,
} from 'store/api/yay/user'
import { ROUTE_PATH } from 'lib/paths'

import Button from 'components/Button'
import EditImage from 'components/EditImage'
import EditWallpaper from 'components/EditWallpaper'
import { FormikInput } from 'components'

import './style.scss'

const MAX_BIOGRAPHY_LIMIT = 500

const UserEditModal = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showConfirmBox } = useGlobalConfirmBox()
  const { upload } = useUploadImage()
  const { isContainBanWord, showDetectedBanWordAlert } = useBanWord()

  const { data: currentUser } = useCurrentUser()

  const [updateUser] = useUpdateUserV3Mutation()
  const [deleteProfileImage] = useDeleteUserProfileImageMutation()
  const [deleteCoverImage] = useDeleteUserCoverImageMutation()

  const initialValues = useMemo(
    () => ({
      nickname: currentUser?.nickname,
      prefecture: currentUser?.prefecture || '',
      biography: currentUser?.biography || '',
      country_code: currentUser?.country_code || 'others',
      profile_icon: currentUser?.profile_icon_thumbnail,
      cover_image: currentUser?.cover_image,
    }),
    [currentUser]
  )

  const saveProfile = async (values: any) => {
    const data: any = {
      nickname: values.nickname,
      biography: values.biography,
    }

    if (values.prefecture && values.prefecture !== initialValues.prefecture)
      data.prefecture = values.prefecture

    if (
      values.country_code &&
      values.country_code !== initialValues.country_code
    ) {
      data.country_code =
        values.country_code === 'others' ? '' : values.country_code
    }

    const _uploads = []
    if (values.profile_icon instanceof File) {
      _uploads.push(
        upload([values.profile_icon], 'user_avatar').then(
          (a) => (data.profile_icon_filename = a[0]?.fileName)
        )
      )
    }
    if (values.cover_image instanceof File) {
      _uploads.push(
        upload([values.cover_image], 'user_cover').then(
          (a) => (data.cover_image_filename = a[0]?.fileName)
        )
      )
    }

    await Promise.all(_uploads)

    const updateUserResponse = await updateUser(data)

    if (initialValues.profile_icon && !values.profile_icon) {
      await deleteProfileImage({})
    }

    if (initialValues.cover_image && !values.cover_image) {
      await deleteCoverImage({})
    }

    if (updateUserResponse?.['data']?.result === 'success') {
      navigate(ROUTE_PATH.USER(currentUser?.id))
    }
  }

  const handleSubmit = async (values: any) => {
    if (isContainBanWord(values.nickname, 'user')) {
      showDetectedBanWordAlert()
      return
    }

    if (initialValues.country_code !== values.country_code) {
      return showConfirmBox({
        content: t('edit_profile_modal.change_country_code_alert.title'),
        confirmBoxProps: {
          onOk: () => saveProfile(values),
        },
      })
    } else {
      return saveProfile(values)
    }
  }

  return (
    <Formik
      validationSchema={Yup.object().shape({
        nickname: Yup.string().required(
          t('edit_profile_modal.nickname_required')
        ),
        biography: Yup.string(),
      })}
      enableReinitialize // Useful in case of user enters directly at this page, so we need to wait user loading be complete to selector update initialValues
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <Form className="Modal__wrapper UserEditModal">
          <div className="Modal__header">
            <Link className="Modal__header__close" to={location.pathname} />

            <h2 className="Modal__header__h2">
              {t('edit_profile_modal.edit_profile')}
            </h2>

            <div className="Modal__header__right">
              <Button type="submit" loading={isSubmitting}>
                {t('common.save')}
              </Button>
            </div>
          </div>

          <div
            className={cx(
              'Modal__body',
              isSubmitting && 'Modal__body--loading'
            )}
          >
            <EditWallpaper
              url={values.cover_image}
              onSelect={(file: any) => {
                setFieldValue('cover_image', file)
              }}
            />

            <EditImage
              id="avatar"
              url={values.profile_icon}
              alt="avatar"
              className="UserEditModal__avatar"
              onSelect={(file) => {
                setFieldValue('profile_icon', file)
              }}
            />

            <div className="UserEditModal__fields">
              <div className="UserEditModal__section">
                <h4>{t('common.nickname')}</h4>
                <FormikInput name="nickname" maxLength={20} />
              </div>

              <div className="UserEditModal__section">
                <h4>{t('common.profile')}</h4>

                <div className="UserEditModal__field--inline">
                  <p>{t('common.gender')}</p>
                  <p>{getGenderLabel(currentUser?.gender)}</p>
                </div>

                <div className="UserEditModal__field--divider" />

                {values.country_code === 'JP' && (
                  <>
                    <div className="UserEditModal__field--inline">
                      <p>{t('common.prefecture')}</p>

                      <FormikInput
                        as="select"
                        name="prefecture"
                        fullWidth={false}
                        size="small"
                        style={{
                          // custom remove letter-spacing only to fix firefox issue
                          // https://jira.nanameue.jp/browse/YAY-14155
                          letterSpacing: 0,
                        }}
                      >
                        <option value="">{t('common.prefecture')}</option>

                        {prefectureList.map((prefecture) => (
                          <option key={prefecture} value={prefecture}>
                            {prefecture}
                          </option>
                        ))}
                      </FormikInput>
                    </div>

                    <div className="UserEditModal__field--divider" />
                  </>
                )}
              </div>

              <div className="UserEditModal__section">
                <h4>{t('common.biography')}</h4>

                <FormikInput
                  as="textarea"
                  rows={5}
                  name="biography"
                  maxLength={MAX_BIOGRAPHY_LIMIT}
                />
              </div>

              <div className="UserEditModal__section">
                <h4>{t('common.country')}</h4>

                <FormikInput as="select" name="country_code">
                  <option value="" disabled>
                    {t('common.country')}
                  </option>

                  {countryCodeList.map((countryCode) => (
                    <option key={countryCode} value={countryCode}>
                      {getCountryLabel(countryCode)}
                    </option>
                  ))}
                </FormikInput>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default UserEditModal
